import {Trans} from 'react-i18next'
import makeStyles from '@material-ui/core/styles/makeStyles'
import messages from '../../../assets/messages'
import {Account} from '../../../graphql/types'

const useStyles = makeStyles((theme) => ({
  pill: {
    // Block so the pill takes a line of its own, but shrink-to-fit so the
    // background hugs the text rather than spanning the whole row.
    display: 'block',
    width: 'fit-content',
    marginTop: 2,
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 400,
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
    // Manager names run long in production (40+ chars). Without a cap, `nowrap`
    // would set the min-content width of the flex row that hosts the pill and
    // push the account balance past the row's clip boundary, hiding it.
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap' as const,
    textOverflow: 'ellipsis',
  },
}))

/** A money manager we have enough of to actually name. */
export type DisplayableMoneyManager = {id: number; name: string};

/**
 * The money manager behind a PAMM investor account, or null when there is none
 * to show — a non-PAMM account, an account not yet matched to a manager, or a
 * manager the PAMM API only half returned (both halves are nullable there, and
 * "MM  - ID 264" helps nobody).
 *
 * Shared so the pill and the picker's search text agree on what counts as a
 * manager: an option must never be findable by a manager it does not display.
 */
export const getPammMoneyManagerInfo = (account: Account): DisplayableMoneyManager | null => {
  const moneyManagerInfo = 'moneyManagerInfo' in account ? account.moneyManagerInfo : null
  if (!moneyManagerInfo?.name || moneyManagerInfo.id == null) return null
  return {id: moneyManagerInfo.id, name: moneyManagerInfo.name}
}

/**
 * Red pill naming the PAMM money manager behind an investor account, so clients
 * holding several PAMM accounts can tell them apart in the account pickers.
 * Renders nothing when there is no manager to name. See PT-5768.
 */
export const PammMoneyManagerPill = ({account}: {account: Account}) => {
  const classes = useStyles()
  const moneyManagerInfo = getPammMoneyManagerInfo(account)

  if (!moneyManagerInfo) return null

  return (
    <span className={classes.pill} data-testid="pamm-money-manager-pill">
      <Trans
        {...messages.pammMoneyManagerPill}
        values={{name: moneyManagerInfo.name, id: moneyManagerInfo.id}}
      />
    </span>
  )
}
